import React, { useState, useEffect } from 'react';
import { VocabularyWord, VocabularyFormData, Language } from '../../types/Vocabulary';
import './VocabularyMatrix.css';

interface VocabularyFormProps {
  word?: VocabularyWord;
  onSave: (data: VocabularyFormData) => Promise<void>;
  onCancel: () => void;
}

const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: 'chinese', label: '中文' },
  { value: 'french', label: 'Français' },
  { value: 'hindi', label: 'हिन्दी' },
  { value: 'english', label: 'English' }
];

const LANGUAGE_LABELS: Record<Language, string> = {
  chinese: '中文',
  french: 'Français',
  hindi: 'हिन्दी',
  english: 'English'
};

const VocabularyForm: React.FC<VocabularyFormProps> = ({ word, onSave, onCancel }) => {
  const [formData, setFormData] = useState<VocabularyFormData>({
    word: word?.word || '',
    language: word?.language || 'english',
    pronunciation: word?.pronunciation || '',
    example_sentence: word?.example_sentence || '',
    notes: word?.notes || '',
    category: word?.category || '',
    translations: word?.translations?.map(t => ({
      translation: t.translation,
      language: t.language
    })) || []
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof VocabularyFormData, value: string | Language) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTranslationChange = (index: number, field: 'translation' | 'language', value: string | Language) => {
    setFormData(prev => {
      const translations = [...(prev.translations || [])];
      translations[index] = {
        ...translations[index],
        [field]: value
      };
      return {
        ...prev,
        translations
      };
    });
  };

  const handleAddTranslation = (language: Language) => {
    setFormData(prev => {
      const existing = prev.translations || [];
      // Check if translation for this language already exists
      if (existing.some(t => t.language === language)) {
        return prev;
      }
      return {
        ...prev,
        translations: [...existing, { translation: '', language }]
      };
    });
  };

  const handleRemoveTranslation = (index: number) => {
    setFormData(prev => {
      const translations = [...(prev.translations || [])];
      translations.splice(index, 1);
      return {
        ...prev,
        translations
      };
    });
  };

  const getAvailableLanguagesForTranslation = (): Language[] => {
    const usedLanguages = new Set<Language>([formData.language]);
    formData.translations?.forEach(t => usedLanguages.add(t.language));
    return LANGUAGE_OPTIONS.filter(opt => !usedLanguages.has(opt.value)).map(opt => opt.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.word.trim()) {
      setError('Word is required');
      return;
    }

    // Filter out empty translations
    const validTranslations = formData.translations?.filter(t => t.translation.trim() !== '') || [];

    setSaving(true);
    try {
      await onSave({
        ...formData,
        translations: validTranslations.length > 0 ? validTranslations : undefined
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save word');
    } finally {
      setSaving(false);
    }
  };

  const availableLanguages = getAvailableLanguagesForTranslation();

  return (
    <div className="vocabulary-form-overlay">
      <div className="vocabulary-form-container">
        <div className="vocabulary-form-header">
          <h2>{word ? 'Edit Word' : 'Add New Word'}</h2>
          <button className="vocabulary-form-close-btn" onClick={onCancel}>×</button>
        </div>

        {error && (
          <div className="vocabulary-form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="vocabulary-form">
          <div className="vocabulary-form-field">
            <label htmlFor="word">Word *</label>
            <input
              id="word"
              type="text"
              value={formData.word}
              onChange={(e) => handleInputChange('word', e.target.value)}
              required
              className="vocabulary-form-input"
            />
          </div>

          <div className="vocabulary-form-field">
            <label htmlFor="language">Language *</label>
            <select
              id="language"
              value={formData.language}
              onChange={(e) => handleInputChange('language', e.target.value as Language)}
              required
              className="vocabulary-form-select"
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="vocabulary-form-field">
            <label htmlFor="pronunciation">Pronunciation</label>
            <input
              id="pronunciation"
              type="text"
              value={formData.pronunciation}
              onChange={(e) => handleInputChange('pronunciation', e.target.value)}
              className="vocabulary-form-input"
            />
          </div>

          <div className="vocabulary-form-field">
            <label htmlFor="example_sentence">Example Sentence</label>
            <textarea
              id="example_sentence"
              value={formData.example_sentence}
              onChange={(e) => handleInputChange('example_sentence', e.target.value)}
              rows={3}
              className="vocabulary-form-textarea"
            />
          </div>

          <div className="vocabulary-form-field">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              rows={3}
              className="vocabulary-form-textarea"
            />
          </div>

          <div className="vocabulary-form-field">
            <label htmlFor="category">Category / Topic</label>
            <input
              id="category"
              type="text"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              placeholder="e.g., Food, Travel, Technology"
              className="vocabulary-form-input"
            />
          </div>

          <div className="vocabulary-form-translations-section">
            <div className="vocabulary-form-translations-header">
              <label>Translations</label>
              {availableLanguages.length > 0 && (
                <div className="vocabulary-form-add-translation-buttons">
                  {availableLanguages.map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => handleAddTranslation(lang)}
                      className="vocabulary-form-add-translation-btn"
                    >
                      + {LANGUAGE_LABELS[lang]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {formData.translations && formData.translations.length > 0 && (
              <div className="vocabulary-form-translations-list">
                {formData.translations.map((trans, index) => (
                  <div key={index} className="vocabulary-form-translation-item">
                    <select
                      value={trans.language}
                      onChange={(e) => handleTranslationChange(index, 'language', e.target.value as Language)}
                      className="vocabulary-form-translation-lang-select"
                    >
                      {LANGUAGE_OPTIONS
                        .filter(opt => opt.value !== formData.language)
                        .map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <input
                      type="text"
                      value={trans.translation}
                      onChange={(e) => handleTranslationChange(index, 'translation', e.target.value)}
                      placeholder="Translation"
                      className="vocabulary-form-translation-input"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveTranslation(index)}
                      className="vocabulary-form-remove-translation-btn"
                      aria-label="Remove translation"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="vocabulary-form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="vocabulary-form-cancel-btn"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="vocabulary-form-save-btn"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VocabularyForm;

